import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import withStyles from 'isomorphic-style-loader/withStyles';
import numeri from 'numeri';
import _ from 'lodash';
import s from './VirusBoard.css';
import mathUtils from '../../core/mathUtils';
import random from '../../core/random';
import { MenuBase, defaultOptWidth, optHeight, optTitleHeight } from '../../core/menuUtils';
import Hierarchy from '../../core/hierarchy';

const xPaddingLeft = 2;
const xPaddingRight = 200;
const yPadding = 15;
const innerXDim = 630;
const innerYDim = 420;
const topBarY = 60;
const bottomBarY = 25;
const barPadding = 5;
const xDim = innerXDim + xPaddingLeft + xPaddingRight;
const yDim = innerYDim + topBarY + bottomBarY + 2 * yPadding + 2 * barPadding;
const zoomOutButtonWidth = 95;
const passButtonWidth = 120;
const topButtonWidth = 30;
const topButtonHeight = 30;
const settingsMenuWidth = 230;
const settingsMenuHeight = 170;
const shareMenuWidth = 380;
const shareMenuHeight = 30;
const defaultBorderColor = '#000000';
const selectedBorderColor = '#eecc00';
const tooltipWidth = 210;
const tooltipHeight = 71;
const nDay = 366;
const qCapRatio = 3;
const endDay = 366;
const menuPadding = 5;
const menuDy = optTitleHeight + optHeight + menuPadding;
const actionButtonWidth = 238;
const actionButtonHeight = 25;
const actionsMenu2Width = 360;
const backButtonWidth = 30;
const backButtonHeight = 30;
const infoMenuMinHeight = 160;
const willPower = 0.5;
const knowSickCurWillRatio = 0.4;
const newDeadWillRatio = 16;
const otherCountryWillRatio = 0.3;
const infoMenuWidth = 350;
const infoMenuHeight = 120;
const chartRowButtons = 7;
const chartMenuWidth = 20 + chartRowButtons * defaultOptWidth;
const chartMenuHeight = 400;
const chartWidth = chartMenuWidth - 20;
const chartLeftPadding = 30;
const chartPadding = 20;
const chartHeight = chartMenuHeight - 2 * menuPadding - 2 * menuDy;
const trophyMenuWidth = 310;
const trophyMenuHeight = 225;
const trophyWidth = 100;
const trophyHeight = 25;
const trophyRowSize = 2;
const refundRatio = 0.8;
const mitiMax = 3072;
const triaMax = 1024;
const dataMax = 2048;
const levelExp = 5;
const areaUnitPerSqKm = 5000000;
const numShapeDepths = 3;
const baseSickKnowRate = 1E-2;
const bulletinDuration = 16;
const ckptDays = [
  0, 7, 14, 21, // jan
  31, 45, // feb
  60, 75, // mar
  91, 106, // apr
  121, 136, // may
  152, 167, // jun
  182, 197, // jul
  213, 228, // aug
  244, 259, // sep
  274, 289, // oct
  305, 320, // nov
  335, 350, // dec
  endDay,
];
const startDate = new Date('2020-01-01T00:00:00Z');
const topMenuKeys = [
  'settingsMenu',
  'shareMenu',
  'chartMenu',
  'infoMenu',
  'trophyMenu',
  'trophyMenu2',
];
const actionMenuKeys = [
  'actionsMenu',
  'actionsMenu2',
];
const menuKeys = topMenuKeys.concat(actionMenuKeys);

const goodColor = numeri.vector([0, 2 / 3, 1 / 3]);
const badColor = numeri.vector([1, 0, 0]);
// f from 1 to 0 if bad
// from 0 to 1 if good
const minNegLog = 13;
const colorEps = 0.1;
const getCityCenters = (cities) => {
  const res = numeri.empty([cities.length, 2]);
  cities.forEach((city, i) => {
    const { meta, parent } = city;
    const parentMeta = parent.meta;
    res.set([i, 0], parentMeta.xMin / parentMeta.scale + meta.x / meta.scale);
    res.set([i, 1], parentMeta.yMin / parentMeta.scale + meta.y / meta.scale);
  });
  return res;
};
const cheatCodes = [
  {
    code: 'seeall',
    effect: (virusBoard) => {
      virusBoard.seeAll = true;
    },
  },
  {
    code: 'knowall',
    effect: (virusBoard) => {
      goalSubsets.forEach((subset) => {
        virusBoard.addResearch(subset, Infinity);
      });
    },
  },
  {
    code: 'doall',
    effect: (virusBoard) => {
      virusBoard.props.unitTree.children.forEach((country) => {
        country.meta.will = Infinity;
      });
    },
  },
];
const colorOpts = [
  {
    text: 'known sick %',
    f: (meta) => {
      const { sick, pop } = meta;
      const frac = sick / pop;

      if (frac === 0) {
        return 1;
      }
      return (1 - colorEps) * Math.min(
          -Math.log(frac) / minNegLog,
          1,
        );
    },
  },
  {
    text: 'dead %',
    f: (meta) => {
      const { dead, pop } = meta;
      const frac = dead / pop;

      if (frac === 0) {
        return 1;
      }
      return (1 - colorEps) * Math.min(
          -Math.log(frac) / minNegLog,
          1,
        );
    },
  },
  {
    text: 'population',
    f: (meta) => {
      const maxPop = meta.isCountry ? 200000000 : 50000000;
      return Math.max(
        0,
        Math.min(
          1,
          1 + Math.log(meta.pop / maxPop) / 3.5,
        ),
      );
    },
  },
  {
    text: 'density',
    f: (meta) => Math.max(
      0,
      Math.min(
        1,
        (Math.log(meta.density) - 17) / 6,
      ),
    ),
  },
  {
    text: 'will',
    f: (meta, unit) => {
      const will = meta.isCountry ? meta.will : unit.parent.meta.will;
      return Math.max(
        0,
        Math.min(
          1,
          Math.log(will + 1) / 9,
        ),
      );
    },
  },
  {
    text: 'wealth',
    f: (meta, unit) => {
      return meta.wealth || unit.parent.meta.wealth;
    },
  },
];
const textOpts = [
  {
    text: 'total',
    f: (numer) => mathUtils.formatLargeNumber(numer),
    numf: (numer) => numer,
  },
  {
    text: 'fraction',
    f: (numer, denom) => mathUtils.formatPercent(numer / denom),
    numf: (numer, denom) => numer / denom,
  },
];

const logZeroStandin = 0.9999999;
const scaleOpts = [
  {
    text: 'total',
    f: (numer) => numer,
    format: mathUtils.formatLargeNumber,
    scale: d3.scaleLinear(),
  },
  {
    text: 'total (log)',
    f: (numer) => numer,
    format: (x) => {
      return x === logZeroStandin ?
        '0' :
        mathUtils.formatLargeNumber(x);
    },
    scale: d3.scaleLog(),
    yMinFn: (yMin) => Math.max(yMin, logZeroStandin),
  },
  {
    text: 'fraction',
    f: (numer, denom) => numer / denom,
    format: mathUtils.formatPercent,
    scale: d3.scaleLinear(),
  },
];
const compareOpts = [
  {
    text: 'phases',
  },
  {
    text: 'regions',
  },
];
const contactTracingMaxDensities = [0, 30, 80, 250];
const contactTracingRatios = [0, 0.5, 0.75, 0.95];

const levelNames = [
  { text: 'none', color: '#000000' },
  { text: 'moderate', color: '#003311' },
  { text: 'high', color: '#006622' },
  { text: 'berserk', color: '#008833' },
];
const severityNames = [
  { text: 'none', color: '#000000' },
  { text: 'moderate', color: '#440000' },
  { text: 'severe', color: '#880000' },
  { text: 'draconian', color: '#bb0000' },
];
const defaultKnownInfectRatio = 0.9;
const knownSubsets = [
  { text: 'apparently healthy', key: 'unknown' },
  { text: 'known sick', key: 'known' },
];
const dirSubsets = [
  { text: 'inward', key: 'in' },
  { text: 'outward', key: 'out' },
];
const suspectedSubsets = [
  { text: 'apparently healthy', key: 'safe' },
  { text: 'suspected sick', key: 'risky' },
];
const goalSubsets = [
  { text: 'virus data', key: 'data', max: dataMax },
  { text: 'mitigation', key: 'miti', max: mitiMax },
  { text: 'hospital resource triaging', key: 'tria', max: triaMax },
];
const goalSubsetsByKey = {};
goalSubsets.forEach((subset) => {
  goalSubsetsByKey[subset.key] = subset;
});
const popPower = 0.8;
const countryActions = [
  {
    key: 'international',
    text: 'international travel restrictions',
    subsets: [dirSubsets, knownSubsets],
    levels: severityNames,
    costFormula: (path, level, pop) => {
      const pathRatio = path[2] === 'known' ? 0.25 : 1;
      return Math.ceil(5E-5 * pop ** popPower * levelExp ** level * pathRatio);
    },
    handle: (virusBoard, unit) => {
      unit.meta.notLibertarian = true;
    },
  },
  {
    key: 'research',
    text: 'research investment',
    subsets: [goalSubsets],
    levels: levelNames,
    costFormula: (path, level, pop, wealth) => {
      return Math.ceil(10 * levelExp ** level / (0.2 + 0.8 * wealth) ** 2);
    },
    selectable: (path, level, research) => {
      const key = path[0];
      return level === 0 || research[key] < goalSubsetsByKey[key].max;
    },
  },
  {
    key: 'tracing',
    text: 'contact tracing',
    subsets: [],
    levels: severityNames,
    costFormula: (path, level, pop, wealth) => {
      return Math.ceil(3 * (Math.log(pop) - 6) * levelExp ** level / (0.3 + 0.7 * wealth));
    },
    handle: (virusBoard, unit, idx) => {
      if (idx === 3) {
        const allDrac = virusBoard.props.unitTree.children.every((country) => {
          return country.meta.act.get(['tracing']) === 3;
        });
        if (allDrac) {
          virusBoard.achieveTrophy(trophiesByKey.surv);
        }
      }
      unit.meta.notLibertarian = true;
    },
  },
];
const cityActions = [
  {
    key: 'q',
    text: 'quarantine',
    subsets: [knownSubsets],
    levels: severityNames,
    costFormula: (path, level, pop) => {
      const pathRatio = path[1] === 'known' ? 0.2 : 1;
      return Math.ceil(5E-5 * pop ** popPower * levelExp ** level * pathRatio);
    },
    handle: (virusBoard, unit) => {
      unit.meta.notLibertarian = true;
    },
  },
  {
    key: 'domestic',
    text: 'domestic travel restrictions',
    subsets: [dirSubsets, knownSubsets],
    levels: severityNames,
    costFormula: (path, level, pop) => {
      const pathRatio = path[2] === 'known' ? 0.5 : 1;
      return Math.ceil(4E-5 * pop ** popPower * levelExp ** level * pathRatio);
    },
    handle: (virusBoard, unit) => {
      unit.meta.notLibertarian = true;
    },
  },
  {
    key: 'testing',
    text: 'testing frequency',
    costFormula: (path, level, pop, wealth) => {
      const pathRatio = path[1] === 'risky' ? 0.2 : 1;
      return Math.ceil(3E-5 * pop ** popPower * levelExp ** level / (1 + wealth) * pathRatio);
    },
    subsets: [suspectedSubsets],
    levels: levelNames,
  },
  {
    key: 'psa',
    text: 'public safety announcements',
    subsets: [],
    levels: levelNames,
    costFormula: (path, level, pop) => {
      return Math.ceil(5E-6 * pop ** popPower * levelExp ** level);
    },
  },
];
const actionsByKey = {};
countryActions.concat(cityActions).forEach((action) => {
  actionsByKey[action.key] = action;
});

const sleepOpts = [
  { text: 'slow', ms: 1000 },
  { text: 'medium', ms: 250 },
  { text: 'vrooom', ms: 1 },
];

const basicValues = [
  { style: 'sick', tensor: 'knowSickCur', text: 'known sick', hist: 'knowSickHist', rgb: [255, 150, 70] },
  { style: 'dead', tensor: 'deadCur', text: 'dead', hist: 'deadHist', rgb: [255, 80, 80] },
  { style: 'reco', tensor: 'knowRecoCur', text: 'known recov', hist: 'knowRecoHist', rgb: [0, 140, 255] },
];

const trophies = [
  {
    key: 'surv',
    text: 'Surveillance State',
    descs: [
      'Doom every country to dystopia by',
      'making a precedent for draconian',
      'contact tracing.',
    ],
    group: 'bad',
  },
  {
    key: 'reck',
    text: 'Reckless',
    descs: [
      'Let at least 5% of the world die.',
    ],
    group: 'bad',
  },
  {
    key: 'depr',
    text: 'Depraved',
    descs: [
      'Let at least 30% of the world die.',
    ],
    group: 'bad',
  },
  {
    key: 'anar',
    text: 'Anarchist',
    descs: [
      'Lose control of a country by unfair,',
      'oppressive treatment combined with',
      'a buildup of political will.',
    ],
    group: 'bad',
    secret: true,
  },
  {
    key: 'ark',
    text: 'Ark City',
    descs: [
      'Keep at least one city entirely',
      'healthy for the whole game, known',
      'or unkown.',
    ],
    group: 'iso',
  },
  {
    key: 'cana',
    text: 'Canary Nation',
    descs: [
      'Keep the virus entirely contained',
      'within the country it started in.',
    ],
    group: 'iso',
  },
  {
    key: 'fors',
    text: 'Canary City',
    descs: [
      'Keep the virus entirely contained',
      'within its original city.',
    ],
    group: 'iso',
  },
  {
    key: 'exp',
    text: 'Plague Slayer',
    descs: [
      'Make sure less than 0.01% of the',
      'world dies.',
    ],
    group: 'comp',
  },
  {
    key: 'libt',
    text: 'Libertarian Dream',
    descs: [
      'Don\'t let big government infringe',
      'on anyone\'s personal freedoms, but',
      'make sure less than 0.1% of the',
      'world dies.',
    ],
    group: 'comp',
    secret: true,
  },
];
const trophiesByKey = {};
trophies.forEach((trophy) => {
  trophiesByKey[trophy.key] = trophy;
});
const trophiesByGroup = _.groupBy(trophies, (trophy) => trophy.group);

const getSubsetCombos = (subsets) => {
  let subsetCombos = [[]];
  for (let depth = 0; depth < subsets.length; depth++) {
    const newCombos = [];
    subsetCombos.forEach((combo) => {
      subsets[depth].forEach((set) => {
        newCombos.push(combo.concat([set]));
      });
    });
    subsetCombos = newCombos;
  }
  return subsetCombos;
};

const buildPath = (d, mult) => {
  if (!d.path) {
    const elems = [];
    d.boundary.forEach((step) => {
      const x = step.j * mult;
      const y = step.i * mult;
      if (step.type === 'init') {
        elems.push(`M ${x} ${y}`);
      } else if (step.type === 'left' || step.type === 'right') {
        elems.push(`H ${x}`);
      } else {
        elems.push(`V ${y}`);
      }
    });
    d.path = elems.join(' ');
  }

  return d.path;
};

const buyActionLevel = (unit, path, ind) => {
  const remainingWill = calcRemainingWill(unit, path, ind);
  const countryMeta = unit.meta.isCountry ?
    unit.meta :
    unit.parent.meta;
  countryMeta.will = remainingWill;
  unit.meta.act.set(path, ind);
};

const calcCost = (unit, path, ind1) => {
  const ind0 = unit.meta.act.get(path);
  const actionKey = path[0];
  const action = actionsByKey[actionKey];
  const wealth = unit.meta.wealth || unit.parent.meta.wealth;
  const costDiff = action.costFormula(path, ind1, unit.meta.pop, wealth) -
    action.costFormula(path, ind0, unit.meta.pop, wealth);
  if (ind1 >= ind0) {
    return costDiff;
  }
  return Math.ceil(costDiff * refundRatio);
};

const calcRemainingWill = (unit, path, ind) => {
  const cost = calcCost(unit, path, ind);
  const countryMeta = unit.meta.isCountry ?
    unit.meta :
    unit.parent.meta;
  return countryMeta.will - cost;
};

const dayToDate = (n) => {
  const res = new Date(startDate);
  res.setUTCDate(res.getUTCDate() + n);
  return res;
};
const formatDate = (date) => date.toISOString().slice(0, 10);
const dayToFormattedDate = (n) => formatDate(dayToDate(n));

class VirusBoard extends MenuBase {
  constructor(props) {
    super(props, { s, xDim, yDim });
  }

  static propTypes = {
    countryNames: PropTypes.arrayOf(PropTypes.string),
    cityNames: PropTypes.arrayOf(PropTypes.string),
    unitTree: PropTypes.any,
    virusData: PropTypes.any,
    rng: PropTypes.any,
    seed: PropTypes.number,
  };

  componentDidMount() {}

  shouldComponentUpdate(nextProps) {
    const { unitTree, cityNames, countryNames, rng } = nextProps;
    if (unitTree && cityNames && countryNames && rng) {
      if (!this.initialized) {
        this.initialize(nextProps);
        return true;
      }
      return false;
    }
    return true;
  }

  componentDidUpdate() {
    if (this.initialized) {
      this.draw();
    }
  }

  unitColor(d) {
    if (d.idx === -1) {
      return '#fff';
    }
    const child = this.currentUnit.children[d.idx];

    const shading = colorOpts[this.shadeOpt];
    const shade01 = shading.f(child.meta, child);
    const baseColor = child.meta.anarchy ?
      badColor :
      goodColor;
    const colors = baseColor
      .map((x) => x * shade01);

    return `rgb(${colors.data.map((x) => Math.round(x * 255)).join(',')})`;
  }

  initialize(props) {
    const { unitTree, cityNames, countryNames, rng, virusData } = props;
    this.date = new Date(startDate.getTime());
    this.step = 0;
    this.textOpt = 0;
    this.shadeOpt = 0;
    this.chartCompareOpt = 0;
    this.chartScaleOpt = 0;
    this.chartRegionInds = new Set([1, 2]);
    this.chartRegionInd = 0;
    this.chartSeriesInds = new Set([0, 1, 2]);
    this.chartSeriesInd = 0;
    this.research = {};
    this.daySpeedOpt = 1;
    this.cheatCodeState = '';
    goalSubsets.forEach((subset) => {
      this.research[subset.key] = 0;
    });
    let cityIdx = 0;
    const popData = [];
    this.cityByIdx = [];
    const hospitalRatioArr = [];
    for (let i = 0; i < unitTree.children.length; i++) {
      const country = unitTree.children[i];
      country.meta.name = countryNames[i];
      for (let j = 0; j < country.children.length; j++) {
        const city = country.children[j];
        city.meta.name = cityNames[cityIdx];
        city.meta.globalIdx = cityIdx;
        popData.push(city.meta.pop);
        cityIdx++;
        this.cityByIdx.push(city);
        hospitalRatioArr.push(country.meta.hospital);
      }
    }
    const nCity = cityIdx;
    this.nCity = nCity;

    unitTree.traverse((unit) => {
      unit.meta.act = new Hierarchy(0);
    });

    const planetRegionOpts = [{
      text: 'Planet',
      inds: numeri.range(nCity).data,
    }];
    const countryRegionOpts = unitTree.children.map((country) => ({
      text: country.meta.name,
      inds: country.children.map((city) => city.meta.globalIdx),
    }));
    const cityRegionOpts = numeri.range(nCity).data.map((i) => ({
      text: this.cityByIdx[i].meta.name,
      inds: [i],
    }));
    this.chartRegionOpts = planetRegionOpts.concat(countryRegionOpts, cityRegionOpts);

    this.sickQLen = virusData.recoDays * qCapRatio;
    const pop = numeri.vector(popData);
    const hospitalRatio = numeri.vector(hospitalRatioArr);
    this.stats = {
      knowSickHist: numeri.zeros([nCity, nDay + 1]),
      knowRecoHist: numeri.zeros([nCity, nDay + 1]),
      deadHist: numeri.zeros([nCity, nDay + 1]),
      pop,
      knowSickCur: numeri.zeros([nCity]),
      knowRecoCur: numeri.zeros([nCity]),
      sickCur: numeri.zeros([nCity]),
      recoCur: numeri.zeros([nCity]),
      deadCur: numeri.zeros([nCity]),
      sickQ: numeri.zeros([nCity, this.sickQLen]),
      knowSickQ: numeri.zeros([nCity, this.sickQLen]),
      baseAdjMat: numeri.zeros([nCity, nCity]),
      hospitalCap: pop.times(hospitalRatio),
      density: numeri.zeros([nCity]),
    };

    const cityCenters = getCityCenters(this.cityByIdx);
    const dists = cityCenters
      .broadcastOn(0)
      .minus(cityCenters.broadcastOn(1))
      .norm({ axes: [2] });
    for (let i = 0; i < nCity; i++) {
      const ci = this.cityByIdx[i];
      for (let j = 0; j < nCity; j++) {
        let adj = 0;
        if (i === j) {
          const density = ci.meta.density;
          const densityTerm = 0.15 * (Math.log(density) - 22);
          adj = 1 + densityTerm;
        } else {
          // adjacency for closeness
          const cj = this.cityByIdx[j];
          // TODO FIX X AND Y OFFSET/SCALE
          const dist = dists.get(i, j);
          adj += 1E-6 / Math.max(dist, 0.2);

          // adjacency for same country
          if (ci.parent === cj.parent) {
            adj += 7E-5;

            // adjacency for bordering within same country
            if (ci.meta.neighbors.has(cj.meta.idx)) {
              adj += 5E-4;
            }
          }

          // adjacency for being in bordering countries
          if (ci.parent.meta.neighbors.has(cj.parent.meta.idx)) {
            adj += 1.5E-5;
          }
        }

        this.stats.baseAdjMat.set([i, j], adj);
      }

      this.stats.density.set([i], ci.meta.density);
    }
    this.stats.adjMats = {
      known: this.stats.baseAdjMat,
      unknown: this.stats.baseAdjMat,
    };

    const initCityIdx = rng.inds(nCity, 1, popData)[0];
    this.stats.deadCur.set([initCityIdx], 1);
    this.stats.deadHist.set([initCityIdx, 0], 1);
    const initCity = this.cityByIdx[initCityIdx];
    const initCountry = this.cityByIdx[initCityIdx].parent;
    initCountry.meta.will = Math.ceil(
      initCity.meta.pop ** 0.7 * 1.3E-4 +
      initCountry.meta.pop ** 0.7 * 2E-5,
    );
    let sickSum = 0;
    let knowSickSum = 0;
    for (let i = 0; i < virusData.recoDays; i++) {
      const sickThisDay = Math.round(
        1 / virusData.deathRate *
        virusData.dailyGrowth **
        (i - virusData.recoDays + virusData.deadDays),
      );
      const knowSickThisDay = mathUtils.binomApprox(sickThisDay, baseSickKnowRate, rng);
      this.stats.sickQ.set(
        [initCityIdx, i],
        sickThisDay,
      );
      this.stats.knowSickQ.set(
        [initCityIdx, i],
        knowSickThisDay,
      );
      sickSum += sickThisDay;
      knowSickSum += knowSickThisDay;
    }
    this.stats.knowSickCur.set([initCityIdx], knowSickSum);
    this.stats.knowSickHist.set([initCityIdx, 0], knowSickSum);
    this.initCityIdx = initCityIdx;
    this.stats.sickCur.set([initCityIdx], sickSum);
    this.calcBasicValues();
    this.day = 0;

    this.initialized = true;
  }

  calcAdjMat() {
    const quarantineMults = [1, 0.5, 0.18, 0.05];
    const domesticMults = [1, 0.3, 0.1, 0.03];
    const internationalMults = [1, 0.3, 0.1, 0.03];
    const psaMults = [1, 0.8, 0.55, 0.3];
    const { nCity, props } = this;
    const { unitTree } = props;

    ['known', 'unknown'].forEach((know) => {
      const mult = numeri.fill([nCity, nCity], 1);
      unitTree.children.forEach((country) => {
        const cityInds = country.children.map((city) => city.meta.globalIdx);
        country.children.forEach((city) => {
          // i, j corresponds to how likely city i is to infect j
          const cityAct = city.meta.act;
          const idx = city.meta.globalIdx;
          const domInMult = domesticMults[cityAct.get(['domestic', 'in', know])];
          const domOutMult = domesticMults[cityAct.get(['domestic', 'out', know])];
          const qMult = quarantineMults[cityAct.get(['q', know])];
          const psaMult = psaMults[cityAct.get(['psa'])] **
            (1 + 1 * this.research.miti / mitiMax);
          if (qMult !== 1) {
            mult.slice(undefined, idx).mapInPlace((x) => x * qMult);
          }
          if (psaMult !== 1) {
            mult.slice(idx, undefined).mapInPlace((x) => x * psaMult);
          }

          cityInds.forEach((idx1) => {
            if (idx1 !== idx) {
              mult.update(
                [idx, idx1],
                (x) => x * domOutMult,
              );
              mult.update(
                [idx1, idx],
                (x) => x * domInMult,
              );
            }
          });
        });

        const cityIndSet = new Set(cityInds);
        const countryAct = country.meta.act;
        const interInMult = internationalMults[countryAct.get(['international', 'in', know])];
        const interOutMult = internationalMults[countryAct.get(['international', 'out', know])];
        cityInds.forEach((idx) => {
          for (let idx1 = 0; idx1 < this.nCity; idx1++) {
            if (!cityIndSet.has(idx1)) {
              mult.update(
                [idx, idx1],
                (x) => x * interOutMult,
              );
              mult.update(
                [idx1, idx],
                (x) => x * interInMult,
              );
            }
          }
        });
      });
      this.stats.adjMats[know] = this.stats.baseAdjMat.times(mult);
    });
  }

  calcBasicValues() {
    const { unitTree } = this.props;
    basicValues.forEach((value) => {
      const statTensor = this.stats[value.tensor];
      let globalSum = 0;
      unitTree.children.forEach((country) => {
        let countrySum = 0;
        country.children.forEach((city) => {
          const x = statTensor.get(city.meta.globalIdx);
          countrySum += x;
          globalSum += x;
          city.meta[value.style] = x;
        });
        country.meta[value.style] = countrySum;
      });
      unitTree.meta[value.style] = globalSum;
    });
  }

  calcStep() {
    // updates hists, curs, and sickQ
    const { rng, virusData } = this.props;
    const { day, nCity } = this;
    const { recoDays, deadDays, incuDays } = virusData;

    // setup
    const newReco = this.stats.sickQ
      .slice(undefined, 0);

    // hists
    this.stats.knowSickCur = this.stats.knowSickQ
      .slice(undefined, [0, recoDays])
      .sum({ axes: [1] });

    // sickQ's
    ['sickQ', 'knowSickQ'].forEach((q) => {
      this.stats[q] = this.stats[q]
      .slice(undefined, [1]); // sickQ, knowSickQ update 1

      if (this.stats[q].shape[1] < recoDays) {
        const newQ = numeri.zeros([nCity, this.sickQLen]);
        newQ
          .slice(undefined, [0, this.stats[q].shape[1]])
          .elemwiseBinaryOpInPlace(
            this.stats[q],
            (irrelevant, old) => old,
          );
        this.stats[q] = newQ; // sickQ, knowSickQ refresh
      }
    });

    const healthyCur = this.stats.pop
      .minus(this.stats.sickCur)
      .minus(this.stats.deadCur)
      .minus(this.stats.recoCur);
    const infectiousness = virusData.infect;
    const unkownSick = this.stats.sickCur.minus(this.stats.knowSickCur);
    const bcastHealthyRatio = healthyCur.div(this.stats.pop).broadcastOn(0);
    const term1 = this.stats.knowSickCur
      .broadcastOn(1)
      .times(bcastHealthyRatio)
      .times(this.stats.adjMats.known)
      .times(defaultKnownInfectRatio ** (1 + 1 * this.research.miti / mitiMax));
    const term2 = unkownSick
      .broadcastOn(1)
      .times(bcastHealthyRatio)
      .times(this.stats.adjMats.unknown);
    const newSick = term1.plus(term2)
      .times(infectiousness)
      .sum({ axes: [0] })
      .elemwiseBinaryOpInPlace(
        healthyCur,
        (expectedNewSick, healthy) => {
          const p = healthy === 0 ? 0 : Math.min(1, expectedNewSick / healthy);
          return mathUtils.binomApprox(healthy, p, rng);
        },
      );

    const deadInd = recoDays - deadDays - 1;
    const dying = this.stats.sickQ
      .slice(undefined, deadInd);

    const triagingRatio = 1 + 4 * this.research.tria / triaMax;
    const effectiveHospitalCap = this.stats.hospitalCap
      .times(triagingRatio);
    const avgDeathRate = this.stats.sickCur
      .elemwiseBinaryOp(
        effectiveHospitalCap,
        (sick, hospitalCap) => sick < hospitalCap ?
            virusData.deathRate :
            (virusData.deathRate * hospitalCap +
              virusData.highDeathRate * (sick - hospitalCap)) /
              sick,
      );
    const newDead = dying
      .elemwiseBinaryOp(
        avgDeathRate,
        (n, p) => mathUtils.binomApprox(n, p, rng),
      );
    const knowNewDead = newDead
      .elemwiseBinaryOp(
        this.stats.knowSickQ.slice(undefined, deadInd).div(dying.plus(1E-6)),
        (n, p) => Math.round(n * p),
      );

    // discoveries through contact tracing
    const knowFracNumer = this.stats.knowSickCur
      .plus(newDead.minus(knowNewDead));
    const knowFrac = knowFracNumer
      .div(this.stats.sickCur.plus(1E-6));
    const contactTracingProbs = numeri.vector(
        this.cityByIdx.map((city) => city.parent.meta.act.get(['tracing'])),
      )
      .elemwiseBinaryOpInPlace(this.stats.density, (l, density) => {
        return contactTracingRatios[l] *
          Math.min(
            density,
            contactTracingMaxDensities[l] * areaUnitPerSqKm,
          ) / density;
      })
      .times(knowFrac);
    const contactTracingDiscoveries = newSick
      .elemwiseBinaryOp(contactTracingProbs, (n, prob) => {
        return mathUtils.binomApprox(n, prob, rng);
      });

    this.stats.knowSickQ
      .slice(undefined, recoDays - 1)
      .elemwiseBinaryOpInPlace(contactTracingDiscoveries, (a, b) => a + b);
    this.stats.sickQ
      .slice(undefined, recoDays - 1)
      .assign(newSick); // sickQ update 2

    // reduce sickQ's for those that died
    this.stats.newDead = newDead;
    dying
      .elemwiseBinaryOpInPlace(
        newDead,
        (a, b) => a - b,
      );
    this.stats.knowSickQ.slice(undefined, deadInd)
      .elemwiseBinaryOpInPlace(
        knowNewDead,
        (a, b) => a - b,
      );

    // knowSickQ update 2
    for (let i = 0; i < nCity; i++) {
      const cityMeta = this.cityByIdx[i].meta;
      let healthyKnowRate;
      let sickKnowRate;
      if (this.seeAll) {
        healthyKnowRate = 1;
        sickKnowRate = 1;
      } else {
        healthyKnowRate = (3 ** cityMeta.act.get(['testing', 'safe']) - 1) * 5E-3;
        sickKnowRate = (3 ** cityMeta.act.get(['testing', 'risky'])) *
          (1 + 2 * this.research.data / dataMax) * baseSickKnowRate;
      }
      const knowSlice = this.stats.knowSickQ.slice(i, [0, recoDays]);
      const knowIncr = this.stats.sickQ
        .slice(i, [0, recoDays])
        .minus(knowSlice);
      knowIncr
        .slice([0, recoDays - incuDays])
        .mapInPlace((n) => mathUtils.binomApprox(
          n,
          sickKnowRate,
          rng,
        ));
      knowIncr
        .slice([recoDays - incuDays, recoDays])
        .mapInPlace((n) => mathUtils.binomApprox(n, healthyKnowRate, rng));
      knowSlice.elemwiseBinaryOpInPlace(
        knowIncr,
        (a, b) => a + b,
      );
    }

    this.stats.sickCur
      .elemwiseBinaryOpInPlace(
        newSick,
        (a, b) => a + b,
      )
      .elemwiseBinaryOpInPlace(
        newDead,
        (a, b) => a - b,
      )
      .elemwiseBinaryOpInPlace(
        newReco,
        (a, b) => a - b,
      ); // sickCur update
    this.stats.knowSickCur.plus(newDead).data.forEach((count, idx) => {
      if (count) {
        const city = this.cityByIdx[idx];
        if (!city.parent.meta.knowGotSick) {
          this.warnCountryBulletin(city.parent);
        }
        city.meta.knowGotSick = true;
        city.parent.meta.knowGotSick = true;
      }
    });
    this.stats.sickCur.data.forEach((count, idx) => {
      if (count) {
        const city = this.cityByIdx[idx];
        city.meta.gotSick = true;
        city.parent.meta.gotSick = true;
      }
    });
    this.stats.deadCur
      .elemwiseBinaryOpInPlace(
        newDead,
        (a, b) => a + b,
      ); // deadCur update
    this.stats.recoCur
      .elemwiseBinaryOpInPlace(
        newReco,
        (a, b) => a + b,
      ); // recoCur update
    this.stats.knowRecoCur = this.stats.knowRecoCur
      .elemwiseBinaryOpInPlace(
        this.stats.knowSickQ
          .slice(undefined, 0),
        (a, b) => a + b,
      ); // knowRecoCur update

    // hist updates
    const series = {
      sick: this.stats.knowSickCur,
      dead: this.stats.deadCur,
      reco: this.stats.knowRecoCur,
    };
    basicValues.forEach((value) => {
      this.stats[value.hist]
        .slice(undefined, day + 1)
        .assign(series[value.style]);
    });

    // research goals
    const researchLevels = [0, 1, 6, 36];
    this.props.unitTree.children.forEach((country) => {
      const { act } = country.meta;
      goalSubsets.forEach((subset) => {
        const path = ['research', subset.key];
        const incr = researchLevels[act.get(path)];
        this.addResearch(subset, incr);
      });
    });
  }

  addResearch(subset, incr) {
    const { key, max, text } = subset;
    const path = ['research', key];
    if (this.research[key] + incr > max) {
      this.research[key] = max;
      this.props.unitTree.children.forEach((country) => {
        buyActionLevel(country, path, 0);
        this.updateBulletin(`Maxed out ${text} research!`, true);
      });
    } else {
      this.research[key] += incr;
    }
  }

  calcPoliticalWill() {
    const { unitTree } = this.props;
    const { knowSickCur, newDead } = this.stats;
    const countries = unitTree.children;
    const cityWillIncr = knowSickCur
      .elemwiseBinaryOp(
        newDead,
        (a, b) => a * knowSickCurWillRatio + b * newDeadWillRatio,
      );
    let totalS = 0;
    countries.forEach((country) => {
      let sum = 0;
      country.children.forEach((city) => {
        const { globalIdx } = city.meta;
        sum += cityWillIncr.get(globalIdx);
      });
      totalS += sum;
      country.meta.will += Math.round(sum ** willPower);
    });
    countries.forEach((country) => {
      country.meta.will += Math.floor(
        totalS ** willPower * country.meta.pop /
        unitTree.meta.pop * otherCountryWillRatio,
      );
    });
  }

  draw() {
    this.svg = d3.select('#virus-panel');

    this.background = this.svg
      .append('rect')
      .attr('width', xDim)
      .attr('height', yDim)
      .attr('fill', '#fff')
      .on('click', () => {
        this.clearKeys(menuKeys);
      });
    this.topG = this.svg
      .append('g')
      .attr('transform', `translate(${xPaddingLeft},${yPadding})`)
      .attr('width', innerXDim)
      .attr('height', topBarY);
    this.mainG = this.svg
      .append('g')
      .attr('transform', `translate(${xPaddingLeft},${yPadding + topBarY + barPadding})`)
      .attr('width', innerXDim)
      .attr('height', innerYDim);
    this.shapeGs = [];
    for (let i = 0; i < numShapeDepths; i++) {
      this.shapeGs.push(
        this.mainG
          .append('g')
          .attr('width', innerXDim)
          .attr('height', innerYDim),
      );
    }
    this.bottomG = this.svg
      .append('g')
      .attr('transform', `translate(${xPaddingLeft},${yPadding + topBarY + innerYDim + 2 * barPadding})`)
      .attr('width', innerXDim)
      .attr('height', yPadding);

    this.drawDate();

    this.placeName = this.topG
      .append('text')
      .attr('class', s.placename)
      .attr('x', 0)
      .attr('y', 0)
      .attr('dominant-baseline', 'hanging')
      .attr('tabindex', '0')
      .on('keypress', () => {
        const { keyCode } = d3.event;
        if ((keyCode >= 65 && keyCode < 91) || (keyCode >= 97 && keyCode < 123)) {
          const char = String.fromCharCode(keyCode).toLowerCase();
          this.cheatCodeState += char;
          const l = this.cheatCodeState.length;
          if (l > 30) {
            this.cheatCodeState = this.cheatCodeState.slice(l - 15);
          }
          console.log('CHEAT CODE STATE', this.cheatCodeState);
          cheatCodes.forEach((cheatCode) => {
            if (cheatCode.code === this.cheatCodeState.slice(
              this.cheatCodeState.length - cheatCode.code.length,
            )) {
              cheatCode.effect(this);
            }
          });
        }
      });

    this.drawButton({
      key: 'zoomOutButton',
      g: this.bottomG,
      x: 0,
      y: 0,
      width: zoomOutButtonWidth,
      height: bottomBarY,
      hidden: true,
      text: 'zoom out ↺',
      onClick: () => this.handleZoomOut(),
    });
    this.bulletin = this.bottomG
      .append('text')
      .attr('dominant-baseline', 'hanging')
      .attr('text-anchor', 'middle')
      .attr('x', innerXDim / 2)
      .attr('y', menuPadding)
      .text('');
    this.warnCountryBulletin(this.cityByIdx[this.initCityIdx].parent);

    this.drawButton({
      key: 'settings',
      g: this.topG,
      x: innerXDim - topButtonWidth,
      y: topBarY - topButtonHeight,
      width: topButtonWidth,
      height: topButtonHeight,
      text: '≡',
      onClick: () => this.toggleSettingsMenu(),
    });
    this.drawButton({
      key: 'share',
      g: this.topG,
      x: innerXDim - 2 * topButtonWidth,
      y: topBarY - topButtonHeight,
      width: topButtonWidth,
      height: topButtonHeight,
      text: '<',
      onClick: () => this.toggleShareMenu(),
    });
    this.drawButton({
      key: 'chart',
      g: this.topG,
      x: innerXDim - 3 * topButtonWidth,
      y: topBarY - topButtonHeight,
      width: topButtonWidth,
      height: topButtonHeight,
      text: '📈',
      onClick: () => this.toggleChartMenu(),
    });
    this.drawButton({
      key: 'info',
      g: this.topG,
      x: innerXDim - topButtonWidth,
      y: topBarY - 2 * topButtonHeight,
      width: topButtonWidth,
      height: topButtonHeight,
      text: 'ℹ',
      onClick: () => this.toggleInfoMenu(),
    });
    this.drawButton({
      key: 'trophy',
      g: this.topG,
      x: innerXDim - 2 * topButtonWidth,
      y: topBarY - 2 * topButtonHeight,
      width: topButtonWidth,
      height: topButtonHeight,
      text: '🏆',
      onClick: () => this.toggleTrophyMenu(),
    });

    this.drawPassButton();
    this.drawUnit(this.props.unitTree);

    this.overlays = this.svg
      .append('g')
      .attr('width', xDim)
      .attr('height', yDim);
  }

  toggleShareMenu() {
    const shouldRender = !this.menuCache.shareMenu;
    this.clearKeys(topMenuKeys);
    if (shouldRender) {
      this.drawMenu({
        key: 'shareMenu',
        x: xPaddingLeft + innerXDim - topButtonWidth,
        y: yPadding + topBarY,
        direction: 'downleft',
        width: shareMenuWidth,
        height: shareMenuHeight,
        populate: (g) => {
          const link = `graphallthethings.com/posts/who?seed=${
            random.seedToHex(this.props.seed)}`;
          g
            .append('a')
            .attr('href', `https://${link}`)
            .append('text')
            .attr('class', s.link)
            .attr('x', 10)
            .attr('y', menuPadding)
            .attr('dominant-baseline', 'hanging')
            .text(link);
        },
      });
    }
  }

  toggleSettingsMenu() {
    const shouldRender = !this.menuCache.settingsMenu;
    this.clearKeys(topMenuKeys);
    if (shouldRender) {
      this.drawMenu({
        key: 'settingsMenu',
        x: xPaddingLeft + innerXDim,
        y: yPadding + topBarY,
        direction: 'downleft',
        width: settingsMenuWidth,
        height: settingsMenuHeight,
        populate: (g) => {
          this.drawOptions({
            g,
            title: 'shade regions by',
            x: 10,
            y: menuPadding,
            opts: colorOpts,
            selectedIdx: this.shadeOpt,
            rowWidth: 3,
            onSelect: (i) => {
              this.shadeOpt = i;
              this.drawUnit(this.currentUnit);
              this.toggleSettingsMenu();
              this.toggleSettingsMenu();
            },
          });
          this.drawOptions({
            g,
            title: 'display stats as',
            x: 10,
            y: menuPadding + menuDy + optHeight,
            opts: textOpts,
            selectedIdx: this.textOpt,
            onSelect: (i) => {
              this.textOpt = i;
              this.toggleSettingsMenu();
              this.toggleSettingsMenu();
              this.drawTopStats();
            },
          });
          this.drawOptions({
            g,
            title: 'day speed',
            x: 10,
            y: menuPadding + 2 * menuDy + optHeight,
            opts: sleepOpts,
            selectedIdx: this.daySpeedOpt,
            onSelect: (i) => {
              this.daySpeedOpt = i;
              this.toggleSettingsMenu();
              this.toggleSettingsMenu();
            },
          });
        },
      });
    }
  }

  toggleChartMenu() {
    const shouldRender = !this.menuCache.chartMenu;
    this.clearKeys(topMenuKeys);
    if (shouldRender) {
      this.drawMenu({
        key: 'chartMenu',
        x: xPaddingLeft + innerXDim - 2 * topButtonWidth,
        y: yPadding + topBarY,
        direction: 'downleft',
        width: chartMenuWidth,
        height: chartMenuHeight,
        populate: (g) => {
          g
            .append('rect')
            .attr('width', chartMenuWidth)
            .attr('height', chartMenuHeight)
            .attr('style', 'opacity:0')
            .on('click', () => {
              this.showChartRegionOptions = false;
              this.menuCache.chartRegionOptions
                .attr('visibility', 'hidden');
            });
          this.drawChart(g);
          this.drawOptions({
            title: 'what to compare',
            g,
            x: 10,
            y: menuPadding,
            opts: compareOpts,
            selectedIdx: this.chartCompareOpt,
            onSelect: (i) => {
              this.chartCompareOpt = i;
              this.toggleChartMenu();
              this.toggleChartMenu();
            },
          });
          this.drawOptions({
            title: 'scale',
            g,
            x: 10 + 3 * defaultOptWidth,
            y: menuPadding,
            opts: scaleOpts,
            selectedIdx: this.chartScaleOpt,
            onSelect: (i) => {
              this.chartScaleOpt = i;
              this.toggleChartMenu();
              this.toggleChartMenu();
            },
          });

          let seriesSelection;
          if (this.chartCompareOpt === 0) {
            seriesSelection = { selectedSet: this.chartSeriesInds, multi: true };
          } else {
            seriesSelection = { selectedIdx: this.chartSeriesInd };
          }
          this.drawOptions({
            title: 'series',
            g,
            x: 10 + 3 * defaultOptWidth,
            y: menuPadding + menuDy,
            opts: basicValues,
            onSelect: (i) => {
              if (this.chartCompareOpt !== 0) {
                this.chartSeriesInd = i;
              }
              this.toggleChartMenu();
              this.toggleChartMenu();
            },
            ...seriesSelection,
          });

          let regionSelection;
          if (this.chartCompareOpt === 1) {
            regionSelection = { selectedSet: this.chartRegionInds, multi: true };
          } else {
            regionSelection = { selectedIdx: this.chartRegionInd };
          }
          this.drawOptions({
            key: 'chartRegionOptions',
            title: '',
            g,
            x: 10,
            y: menuPadding + menuDy,
            rowWidth: chartRowButtons,
            opts: this.chartRegionOpts,
            onSelect: (i) => {
              if (this.chartCompareOpt !== 1) {
                this.chartRegionInd = i;
              }
              this.toggleChartMenu();
              this.toggleChartMenu();
            },
            ...regionSelection,
          });
          this.menuCache.chartRegionOptions
            .attr('visibility', this.showChartRegionOptions ? 'visible' : 'hidden');
          this.drawButton({
            g,
            x: 10,
            y: menuPadding + menuDy,
            width: 90,
            height: 20,
            text: 'region(s)▼',
            fontSize: 16,
            textClazz: s.opttitle,
            onClick: () => {
              this.showChartRegionOptions = !this.showChartRegionOptions;
              if (this.showChartRegionOptions) {
                this.menuCache.chartRegionOptions.raise();
              }
              this.menuCache.chartRegionOptions
                .attr('visibility', this.showChartRegionOptions ? 'visible' : 'hidden');
            },
          });
        },
      });
    }
  }

  drawChart(g) {
    const chartG = g
      .append('g')
      .attr('transform', `translate(10,${menuPadding + 2 * menuDy})`);

    chartG
      .append('path')
      .attr('class', s.divider)
      .attr('d', `M 0 0 H ${chartWidth}`);

    const errorLines = [];
    if (this.chartCompareOpt === 0 && this.chartSeriesInds.size === 0) {
      errorLines.push('Need at least 1 phase to plot');
    } else if (this.chartCompareOpt === 1 && this.chartRegionInds.size === 0) {
      errorLines.push('Need at least 1 region to plot');
    }
    if (this.day === 0) {
      errorLines.push('Cannot draw chart on day 1');
    }
    errorLines.forEach((line, i) => {
      chartG
        .append('text')
        .attr('y', 200 + 20 * i)
        .attr('style', 'fill:red')
        .attr('dominant-baseline', 'hanging')
        .text(line);
    });
    if (errorLines.length > 0) {
      return;
    }

    const xScale = d3.scaleLinear()
      .domain([0, this.day])
      .range([chartLeftPadding, chartWidth - chartPadding]);
    const scaleOpt = scaleOpts[this.chartScaleOpt];

    const regionInds = this.chartCompareOpt === 1 ?
      Array.from(this.chartRegionInds) :
      [this.chartRegionInd];
    const seriesInds = this.chartCompareOpt === 0 ?
      Array.from(this.chartSeriesInds) :
      [this.chartSeriesInd];
    const regions = regionInds.map((ind) => this.chartRegionOpts[ind]);
    regions.forEach((region) => {
      if (!region.pop) {
        let pop = 0;
        region.inds.forEach((ind) => {
          pop += this.stats.pop.get(ind);
        });
        region.pop = pop;
      }
    });

    let yMin = 0;
    let yMax = 0;
    const data = [];
    const colors = [];
    const names = [];
    seriesInds.forEach((sInd) => {
      const basicValue = basicValues[sInd];
      const hist = this.stats[basicValue.hist];
      regions.forEach((region, regionI) => {
        const numer = numeri.zeros([this.day + 1]);
        region.inds.forEach((rInd) => {
          numer.elemwiseBinaryOpInPlace(
            hist.slice(rInd, [0, this.day + 1]),
            (x, y) => x + y,
          );
        });
        const pop = region.pop;
        const mapped = numer.mapInPlace((x) => scaleOpt.f(x, pop));
        yMax = Math.max(yMax, mapped.max());
        yMin = Math.min(yMin, mapped.min());
        data.push(mapped.data);
        const rgb = basicValue.rgb.map(
          (x) => Math.round(x * (1 - regionI / Math.max(1, regions.length - 1))),
        );
        colors.push(`rgb(${rgb.join(',')})`);
        names.push(this.chartCompareOpt === 0 ? basicValue.text : region.text);
      });
    });

    if (scaleOpt.yMinFn) {
      yMin = scaleOpt.yMinFn(yMin, yMax);
      data.forEach((seq) => {
        for (let i = 0; i < seq.length; i++) {
          if (seq[i] < yMin) {
            seq[i] = yMin;
          }
        }
      });
    }
    const yScale = scaleOpt.scale
      .domain([yMin, yMax])
      .range([chartHeight - chartPadding, chartPadding]);

    const line = d3.line()
      .x((d, i) => xScale(i))
      .y((d) => yScale(d));

    chartG
      .selectAll(`.${s.line}`)
      .data(data)
      .enter()
      .append('path')
      .attr('id', (d, i) => `series-${i}`)
      .attr('pointer-events', 'none')
      .attr('class', s.line)
      .attr('style', (d, i) => `stroke:${colors[i]}`)
      .attr('d', line);

    let currentInd;
    chartG
      .append('rect')
      .attr('x', chartLeftPadding)
      .attr('y', chartPadding)
      .attr('style', 'opacity:0')
      .attr('width', chartWidth - chartLeftPadding - chartPadding)
      .attr('height', chartHeight - 2 * chartPadding)
      .on('mousemove', () => {
        const { event } = d3;
        const i = Math.round(xScale.invert(
          event.x - event.target.parentElement.getBoundingClientRect().left,
        ));
        if (i >= data[0].length) {
          return;
        }
        const y = event.y - event.target.parentElement.getBoundingClientRect().top;
        const seriesInd = numeri.vector(data.map((series) =>
          Math.abs(yScale(series[i]) - y),
        )).argmin();
        if (seriesInd !== currentInd) {
          if (currentInd !== undefined) {
            chartG
              .select(`#series-${currentInd}`)
              .attr('class', s.line);
          }
          chartG
            .select(`#series-${seriesInd}`)
            .attr('class', s.linehover);
          currentInd = seriesInd;
        }
        const series = data[seriesInd];
        this.chartLegendText
          .text(names[seriesInd])
          .attr('style', `fill:${colors[seriesInd]}`);
        this.chartLegendX
          .text(`x: ${dayToFormattedDate(i)}`);
        this.chartLegendY
          .text(`y: ${scaleOpt.format(series[i])}`);
      })
      .on('mouseout', () => {
        if (currentInd !== undefined) {
          chartG
            .select(`#series-${currentInd}`)
            .attr('class', s.line);
          currentInd = undefined;
        }
        this.chartLegendText
          .text('');
        this.chartLegendX
          .text('');
        this.chartLegendY
          .text('');
      })
      .on('click', () => {
        this.showChartRegionOptions = false;
        this.chartRegionOptions
          .attr('visibility', 'hidden');
      });

    const nXTicks = 7;
    chartG
      .append('g')
      .attr('transform', `translate(0, ${chartHeight - chartPadding})`)
      .call(d3
        .axisBottom()
        .scale(xScale)
        .tickValues(numeri.range(nXTicks).map((x) => Math.round(x * this.day / (nXTicks - 1))).data)
        .tickFormat(dayToFormattedDate),
      );
    chartG
      .append('g')
      .attr('transform', `translate(${chartLeftPadding}, 0)`)
      .call(d3
        .axisLeft()
        .scale(yScale)
        .ticks(5)
        .tickFormat((d) => scaleOpt.format(d)),
      );

    const title = this.chartCompareOpt === 0 ?
      this.chartRegionOpts[this.chartRegionInd].text :
      basicValues[this.chartSeriesInd].text;
    chartG
      .append('text')
      .attr('x', chartWidth / 2)
      .attr('y', 0)
      .attr('dominant-baseline', 'hanging')
      .attr('text-anchor', 'middle')
      .attr('class', s.opttitle)
      .attr('font-size', 18)
      .text(title);
    this.chartLegendText = chartG
      .append('text')
      .attr('x', 40)
      .attr('y', 0)
      .attr('dominant-baseline', 'hanging')
      .attr('font-size', 18)
      .attr('pointer-events', 'none')
      .text('');
    this.chartLegendX = chartG
      .append('text')
      .attr('x', 40)
      .attr('y', 20)
      .attr('dominant-baseline', 'hanging')
      .attr('pointer-events', 'none')
      .text('');
    this.chartLegendY = chartG
      .append('text')
      .attr('x', 40)
      .attr('y', 38)
      .attr('dominant-baseline', 'hanging')
      .attr('pointer-events', 'none')
      .text('');
  }

  toggleInfoMenu() {
    const shouldRender = !this.menuCache.infoMenu;
    this.clearKeys(topMenuKeys);
    const dy = 20;
    const { props } = this;
    const virusDataResearched = this.research.data;
    const { incuDays, deadDays, recoDays, deathRate, highDeathRate } = props.virusData;
    if (shouldRender) {
      this.drawMenu({
        key: 'infoMenu',
        x: xPaddingLeft + innerXDim,
        y: yPadding + topBarY,
        direction: 'downleft',
        width: infoMenuWidth,
        height: infoMenuHeight,
        populate: (g) => {
          const incuText = virusDataResearched >= 256 ?
            incuDays : '?';
          const deadText = virusDataResearched >= 512 ?
            deadDays : '?';
          const recoText = virusDataResearched >= 1024 ?
            recoDays : '?';
          const drText0 = virusDataResearched >= 2048 ?
            mathUtils.formatPercent(deathRate) : '?';
          const drText1 = virusDataResearched >= 2048 ?
            mathUtils.formatPercent(highDeathRate) : '?';
          [
            `people show symptoms ${incuText} days after infection`,
            `people die ${deadText} days after infection`,
            `people recover ${recoText} days after infection`,
            `hospitalized death rate: ${drText0}`,
            `unhospitalized death rate: ${drText1}`,
          ].forEach((line, i) => {
            g
              .append('text')
              .attr('x', 10)
              .attr('y', menuPadding + i * dy)
              .attr('dominant-baseline', 'hanging')
              .text(line);
          });
          g
            .append('text')
            .attr('x', infoMenuWidth / 2)
            .attr('y', menuPadding + 5 * dy)
            .attr('font-size', 10)
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'hanging')
            .text('(fund virus data research to see)');
        },
      });
    }
  }

  toggleTrophyMenu() {
    const shouldRender = !this.menuCache.trophyMenu;
    this.clearKeys(topMenuKeys);
    if (shouldRender) {
      this.drawMenu({
        key: 'trophyMenu',
        x: xPaddingLeft + innerXDim - topButtonWidth,
        y: yPadding + topBarY,
        direction: 'downleft',
        width: trophyMenuWidth,
        height: trophyMenuHeight,
        populate: (g) => {
          this.drawTrophies({
            g,
            x: 10,
            y: menuPadding + 5,
            trophyGroup: trophiesByGroup.comp,
          });
          this.drawTrophies({
            g,
            x: 10,
            y: menuPadding + 55,
            trophyGroup: trophiesByGroup.iso,
          });
          this.drawTrophies({
            g,
            x: 10,
            y: menuPadding + 130,
            trophyGroup: trophiesByGroup.bad,
          });

          const disclaimerLines = [
            'The game cannot be won or lost.',
            'You can only acquire achievements, screenshot them, and share.',
          ];
          disclaimerLines.forEach((line, i) => {
            g
              .append('text')
              .attr('x', 10)
              .attr('y', trophyMenuHeight - menuPadding - (disclaimerLines.length - i) * 11)
              .attr('dominant-baseline', 'hanging')
              .attr('font-size', 10)
              .text(line);
          });
        },
      });
    }
  }

  drawTrophies(args) {
    const { g, x, y, trophyGroup } = args;
    trophyGroup.forEach((trophy, i) => {
      const tx = x + trophyWidth * (i % trophyRowSize);
      const ty = y + trophyHeight * Math.floor(i / trophyRowSize);
      const gClazz = trophy.done ? undefined : s.notdone;
      this.drawButton({
        g,
        x: tx,
        y: ty,
        text: `🏆 ${trophy.text}`,
        textSize: 10,
        width: trophyWidth,
        height: trophyHeight,
        gClazz,
        onClick: () => this.showTrophy(trophy),
      });
    });
  }

  showTrophy(trophy) {
    this.drawMenu({
      key: 'trophyMenu2',
      x: xPaddingLeft + innerXDim - topButtonWidth,
      y: yPadding + topBarY,
      direction: 'downleft',
      width: trophyMenuWidth,
      height: trophyMenuHeight,
      populate: (g) => {
        g
          .append('text')
          .attr('x', trophyMenuWidth / 2)
          .attr('y', menuPadding)
          .attr('font-size', 18)
          .attr('class', s.opttitle)
          .attr('dominant-baseline', 'hanging')
          .attr('text-anchor', 'middle')
          .text(`🏆 ${trophy.text}`);
        g
          .append('text')
          .attr('x', 10)
          .attr('y', menuPadding + 24)
          .attr('class', s.opttitle)
          .attr('dominant-baseline', 'hanging')
          .attr('fill', trophy.done ? '#00aa55' : 'red')
          .text(trophy.done ? 'achieved' : 'incomplete');
        const descs = trophy.done || !trophy.secret ?
          trophy.descs : ['???'];
        descs.forEach((desc, i) => {
          g
            .append('text')
            .attr('x', 10)
            .attr('y', menuPadding + 50 + 20 * i)
            .attr('dominant-baseline', 'hanging')
            .text(desc);
        });
        this.drawBackButton(g, trophyMenuHeight, 'trophyMenu2');
      },
    });
  }

  handleZoomOut() {
    if (this.currentUnit.parent) {
      this.clearKeys(actionMenuKeys);
      this.drawUnit(this.currentUnit.parent);
    }
  }

  checkAnarchy() {
    const { unitTree, rng } = this.props;

    unitTree.children.forEach((country) => {
      const { will, pop, act, anarchy } = country.meta;
      if (anarchy) {
        return;
      }

      let unrest = 0;
      unrest += act.get(['tracing']) === 3;
      unrest += act.get(['international', 'out', 'unknown']) > (act.get(['international', 'out', 'known']) + 1);
      country.children.forEach((child) => {
        const cAct = child.meta.act;
        unrest += 0.5 * (cAct.get(['q', 'unknown']) > cAct.get(['q', 'known'] + 1));
        unrest += 0.5 * (cAct.get(['domestict', 'out', 'unknown']) > cAct.get(['q', 'out', 'known'] + 1));
      });
      if (will > pop * 5E-4 && unrest >= 2.5) {
        country.meta.anarchy = true;
        country.children.forEach((child) => {
          child.meta.anarchy = true;
        });
        countryActions.forEach((action) => {
          getSubsetCombos(action.subsets).forEach((combo) => {
            const path = [action.key].concat(combo.map((x) => x.key));
            const level = rng.float(0, 1) < 0.5 ? 0 : 3;
            buyActionLevel(country, path, level);
          });
        });
        cityActions.forEach((action) => {
          getSubsetCombos(action.subsets).forEach((combo) => {
            const path = [action.key].concat(combo.map((x) => x.key));
            const level = rng.float(0, 1) < 0.5 ? 0 : 3;
            country.children.forEach((city) => {
              buyActionLevel(city, path, level);
            });
          });
        });
        this.achieveTrophy(trophiesByKey.anar);
      }
    });
  }

  async handlePass() {
    if (this.updating || this.step >= ckptDays.length - 1) {
      return;
    }
    this.updating = true;
    this.clearKey('passButton');
    const nDays = ckptDays[this.step + 1] - ckptDays[this.step];

    for (let i = 0; i < nDays; i++) {
      this.calcAdjMat();
      this.calcStep();
      this.calcBasicValues();
      this.calcPoliticalWill();
      this.checkAnarchy();
      this.checkPassTrophies();
      this.date.setUTCDate(this.date.getUTCDate() + 1);
      this.day++;

      this.drawDate();
      this.updateShapeColors();
      this.maybeClearBulletin();

      if (i < nDays - 1) {
        await new Promise(resolve => setTimeout(resolve, sleepOpts[this.daySpeedOpt].ms));
      }
    }
    this.step++;
    this.updating = false;
    if (this.step === ckptDays.length - 1) {
      this.checkEndTrophies();
    } else {
      this.drawPassButton();
    }
  }

  warnCountryBulletin(country) {
    this.updateBulletin(`Country ${country.meta.name} has its first known case!`);
  }

  updateBulletin(text, good) {
    this.lastBulletinDate = this.day;
    this.bulletin
      .attr('class', good ? s.good : s.dead)
      .text(text);
  }

  maybeClearBulletin() {
    if (this.lastBulletinDate <= this.day - bulletinDuration) {
      this.bulletin.text('');
    }
  }

  checkPassTrophies() {
    const { unitTree } = this.props;

    if (unitTree.meta.dead / unitTree.meta.pop >= 0.05) {
      this.achieveTrophy(trophiesByKey.reck);
    }
    if (unitTree.meta.dead / unitTree.meta.pop >= 0.3) {
      this.achieveTrophy(trophiesByKey.depr);
    }
  }

  checkEndTrophies() {
    const { unitTree } = this.props;

    let nSickCities = 0;
    let nSickCountries = 0;
    let nCities = 0;
    unitTree.children.forEach((country) => {
      country.children.forEach((city) => {
        nSickCities += city.meta.gotSick || 0;
        nCities += 1;
      });
      nSickCountries += country.meta.gotSick || 0;
    });
    if (nSickCities === 1) {
      this.achieveTrophy(trophiesByKey.fors);
    }
    if (nSickCountries === 1) {
      this.achieveTrophy(trophiesByKey.cana);
    }
    if (nSickCities < nCities) {
      this.achieveTrophy(trophiesByKey.ark);
    }

    if (unitTree.meta.dead / unitTree.meta.pop < 1E-3) {
      let libertarian = true;
      unitTree.traverse((unit) => {
        if (unit.meta.notLibertarian) {
          libertarian = false;
        }
      });
      if (libertarian) {
        this.achieveTrophy(trophiesByKey.libt);
      }
    }
    if (unitTree.meta.dead / unitTree.meta.pop < 1E-4) {
      this.achieveTrophy(trophiesByKey.exp);
    }
  }

  achieveTrophy(trophy) {
    trophy.done = true;
    if (this.trophyMenu && !this.trophyMenu2) {
      this.toggleTrophyMenu();
      this.toggleTrophyMenu();
    }
  }

  drawDate() {
    if (this.dateText) {
      this.dateText.remove();
    }

    this.dateText = this.topG
      .append('text')
      .attr('class', s.date)
      .attr('x', 0)
      .attr('y', 30)
      .attr('text-anchor', 'start')
      .attr('dominant-baseline', 'hanging')
      .text(formatDate(this.date));
  }

  drawTopStats() {
    if (this.topStats) {
      this.topStats.remove();
    }

    const pop = this.currentUnit.meta.pop;
    this.topStats = this.topG
      .append('text')
      .attr('x', 0)
      .attr('y', topBarY)
      .text(`population: ${mathUtils.formatLargeNumber(pop)}`);
    basicValues.forEach((value) => {
      const count = this.currentUnit.meta[value.style];
      const formatted = textOpts[this.textOpt].f(count, pop);
      this.topStats
        .append('tspan')
        .attr('dx', 9)
        .attr('class', s[value.style])
        .text(`${value.text}: ${formatted}`);
    });
  }

  drawTooltip(event, subunit) {
    const bounds = d3.event.target.parentElement.getBoundingClientRect();
    const { x, y } = event;
    const transX = x - bounds.left;
    const transY = Math.min(y - bounds.top, innerYDim - tooltipHeight);
    this.tooltip = this.mainG
      .append('g')
      .attr('class', s.tooltip)
      .attr('width', tooltipWidth)
      .attr('height', tooltipHeight)
      .attr('pointer-events', 'none')
      .attr('transform', `translate(${transX},${transY})`);
    this.tooltip
      .append('rect')
      .attr('width', tooltipWidth)
      .attr('height', tooltipHeight);
    this.tooltipTitle = this.tooltip
      .append('text')
      .attr('x', 10)
      .attr('y', 20)
      .text(subunit.meta.name);
    const pop = subunit.meta.pop;
    const statsLine = this.tooltip
      .append('text')
      .attr('x', 10)
      .attr('y', 40)
      .text(mathUtils.formatLargeNumber(subunit.meta.pop));
    ['sick', 'dead', 'reco'].forEach((key) => {
      const count = subunit.meta[key];
      statsLine
        .append('tspan')
        .attr('dx', 9)
        .attr('class', s[key])
        .text(textOpts[this.textOpt].f(count, pop));
    });
    const willText = subunit.meta.anarchy ?
      'anarchy' :
      subunit.meta.isCountry ?
        `political will: ${subunit.meta.will}` :
        `political will (shared): ${subunit.parent.meta.will}`;
    this.tooltip
      .append('text')
      .attr('x', 10)
      .attr('y', 60)
      .append('tspan')
      .attr('class', subunit.meta.anarchy ? s.dead : undefined)
      .text(willText);
  }

  drawPassButton() {
    const nextDate = dayToDate(ckptDays[this.step + 1]);

    this.drawButton({
      key: 'passButton',
      g: this.bottomG,
      x: innerXDim - passButtonWidth,
      y: 0,
      width: passButtonWidth,
      height: bottomBarY,
      hidden: this.updating || this.step === ckptDays.length - 1,
      text: `→ ${formatDate(nextDate)}`,
      onClick: () => this.handlePass(),
    });
  }

  drawWillLine() {
    if (this.willLine) {
      this.willLine.remove();
      delete this.willLine;
    }
    const { meta } = this.currentUnit;
    if (meta.isCountry) {
      this.willLine = this.topG
        .append('text')
        .attr('class', meta.anarchy ? s.dead : s.will)
        .attr('x', 90)
        .attr('y', 30)
        .attr('text-anchor', 'start')
        .attr('dominant-baseline', 'hanging')
        .text(meta.anarchy ? 'anarchy' : `political will: ${meta.will}`);
    }
  }

  drawUnit(unit) {
    this.currentUnit = unit;
    this.placeName
      .text(unit.meta.name);
    this.drawWillLine();
    this.menuCache.zoomOutButton
      .attr('visibility', unit.parent ? 'show' : 'hidden');
    const [yh, xh] = unit.pixelMap.shape;
    const mult = Math.min(
      innerXDim / xh,
      innerYDim / yh,
    );
    if (this.depthGroups) {
      this.depthGroups.forEach((depthGroup) => {
        if (depthGroup) {
          depthGroup.remove();
        }
      });
    }
    if (this.tooltip) {
      this.tooltip.remove();
      this.tooltip = undefined;
    }

    this.depthGroups = Array(numShapeDepths).fill(undefined);
    Object.entries(_.groupBy(unit.shape4s, (shape) => shape.depth)).forEach((group) => {
      const [depth, shapes] = group;
      if (depth >= numShapeDepths) {
        return;
      }
      this.depthGroups[depth] = this.shapeGs[depth]
        .selectAll(`.${s.subunit}`)
        .data(shapes)
        .enter()
        .append('path')
        .attr('class', s.subunit)
        .attr('d', (d) => buildPath(d, mult))
        .attr('stroke', defaultBorderColor)
        .attr('stroke-width', '1px')
        .on('mouseover', (d) => {
          if (d.idx === -1) {
            return;
          }

          const child = this.currentUnit.children[d.idx];
          this.shapeGs.forEach((otherG) => {
            otherG
              .selectAll(`path.${s.subunit}`)
              .filter((d0) => d0.idx === d.idx)
              .raise()
              .attr('stroke', selectedBorderColor)
              .attr('stroke-width', '5px');
          });
          this.drawTooltip(event, child);
        })
        .on('mouseout', (d) => {
          if (d.idx === -1) {
            return;
          }

          this.shapeGs.forEach((otherG) => {
            otherG
              .selectAll(`path.${s.subunit}`)
              .filter((d0) => d0.idx === d.idx)
              .attr('stroke', defaultBorderColor)
              .attr('stroke-width', '1px');
          });
          this.tooltip
            .remove();
          delete this.tooltip;
        })
        .on('click', (d) => {
          this.drawActionsMenu(event, d);
        });
    });

    this.updateShapeColors();
  }

  drawActionsMenu(event, d) {
    this.clearKeys(['actionsMenu', 'actionsMenu2']);

    const bounds = d3.event.target.parentElement.getBoundingClientRect();
    const { x: eventX, y: eventY } = event;
    const x = eventX - bounds.left;
    const y = eventY - bounds.top;

    const buttons = [];
    let actions;
    const unit = this.currentUnit.children[d.idx];
    if (unit.meta.isCountry) {
      buttons.push({
        text: 'zoom in 🔍',
        onClick: () => {
          this.drawUnit(unit);
          this.clearKey('actionsMenu');
        },
      });
      actions = countryActions;
    } else {
      actions = cityActions;
    }
    const nButton = 1 + actions.length + buttons.length;
    const height = nButton * actionButtonHeight;
    buttons.push({
      text: 'extra info',
      onClick: () => {
        this.drawInfoMenu(unit, x, y, Math.max(height, infoMenuMinHeight));
      },
    });
    actions.forEach((action) => {
      buttons.push({
        text: `• ${action.text}`,
        onClick: () => {
          this.drawActionsMenu2(unit, x, y, height, action);
        },
      });
    });

    this.drawMenu({
      key: 'actionsMenu',
      x,
      y,
      direction: 'rightdown',
      width: actionButtonWidth,
      height,
      populate: (g) => {
        buttons.forEach((button, i) => {
          this.drawButton(Object.assign(
            {
              g,
              x: 0,
              y: i * actionButtonHeight,
              width: actionButtonWidth,
              height: actionButtonHeight,
              textAnchor: 'start',
            },
            button,
          ));
        });
      },
    });
  }

  drawInfoMenu(unit, x, y, height) {
    this.drawMenu({
      key: 'actionsMenu2',
      x,
      y,
      width: actionButtonWidth,
      height,
      direction: 'rightdown',
      populate: (g) => {
        const { wealth, name, isCountry, area, pop, hospital } = unit.meta;
        let wealthStr;
        if (wealth < 0.1) {
          wealthStr = 'very poor';
        } else if (wealth < 0.3) {
          wealthStr = 'poor';
        } else if (wealth < 0.7) {
          wealthStr = 'moderate';
        } else if (wealth < 0.8) {
          wealthStr = 'rich';
        } else {
          wealthStr = 'very rich';
        }
        const areaSqKm = area * areaUnitPerSqKm;
        const densitySqKm = pop / areaSqKm;
        const lines = [
          `name: ${name}`,
          `type: ${isCountry ? 'country' : 'city'}`,
          `area: ${Math.round(areaSqKm)}km²`,
          `density: ${Math.round(densitySqKm)}/km²`,
          `wealth: ${wealthStr}`,
          `hospital beds: ${Math.round((hospital || unit.parent.meta.hospital) * pop)}`,
        ];
        lines.forEach((line, i) => {
          const textY = menuPadding + i * 20;
          g
            .append('text')
            .attr('x', 10)
            .attr('y', textY)
            .attr('dominant-baseline', 'hanging')
            .text(line);
        });
        this.drawBackButton(g, height, 'actionsMenu2');
      },
    });
  }

  drawActionsMenu2(unit, x, y, prevHeight, action) {
    this.clearKey('actionsMenu2');

    const { key, text, subsets, levels } = action;
    const inds = unit.meta.act.subh([key]);

    const subsetCombos = getSubsetCombos(subsets);

    const height = Math.max(prevHeight, 80 + menuDy * subsetCombos.length);
    this.drawMenu({
      key: 'actionsMenu2',
      x,
      y,
      width: actionsMenu2Width,
      height,
      direction: 'rightdown',
      populate: (g) => {
        g
          .append('text')
          .attr('x', actionsMenu2Width / 2)
          .attr('y', menuPadding)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'hanging')
          .text(text);
        const willStr = unit.meta.anarchy ?
          'anarchy' :
          `political will: ${
          (unit.meta.isCountry ? unit.meta.will : unit.parent.meta.will).toString()}`;
        g
          .append('text')
          .attr('class', unit.meta.anarchy ? s.dead : undefined)
          .attr('x', actionsMenu2Width / 2)
          .attr('y', 25)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'hanging')
          .text(willStr);

        subsetCombos.forEach((combo, i) => {
          const path = combo.map((elem) => elem.key);
          const fullPath = [key].concat(path);
          const selectedIdx = inds.get(path);
          const opts = levels.map((level, j) => {
            const selectable = !unit.meta.anarchy &&
              calcRemainingWill(unit, fullPath, j) >= 0 &&
              (!action.selectable || action.selectable(path, j, this.research));
            return Object.assign(
              { selectable },
              level,
              { text: j === selectedIdx ? level.text : `${level.text} (${calcCost(unit, fullPath, j)})` },
            );
          });
          this.drawOptions({
            g,
            title: combo.map((elem) => elem.text).join(', '),
            x: 10,
            y: 45 + menuDy * i,
            opts,
            selectedIdx,
            optWidth: 85,
            onSelect: (j) => {
              buyActionLevel(unit, fullPath, j);
              this.drawWillLine();
              if (this.shadeOpt === 4) {
                // coloring by will; gotta refresh
                this.updateShapeColors();
              }
              this.drawActionsMenu2(unit, x, y, height, action);
              if (action.handle) {
                action.handle(this, unit, j, combo);
              }
            },
          });
        });

        this.drawBackButton(g, height, 'actionsMenu2');
      },
    });
  }

  drawBackButton(g, height, key) {
    this.drawButton({
      g,
      x: 0,
      y: height - backButtonHeight,
      width: backButtonWidth,
      height: backButtonHeight,
      text: '◀',
      onClick: () => this.clearKey(key),
    });
  }

  updateShapeColors() {
    this.drawTopStats();
    this.drawWillLine();
    this.depthGroups.forEach((depthGroup) => {
      if (depthGroup) {
        depthGroup.attr(
          'fill',
          (d) => this.unitColor(d),
        );
      }
    });
  }

  render() {
    if (this.initialized) {
      return (
        <svg id="virus-panel" width={xDim} height={yDim} />
      );
    }
    return (
      <svg width={xDim} height={yDim}>
        <g transform="translate(50, 100)">
          <text>Loading!</text>
        </g>
      </svg>
    );
  }
}

export default withStyles(s)(VirusBoard);



// WEBPACK FOOTER //
// src/components/Virus/VirusBoard.js